/**
 * POC animated lobby — the DOM (CSS keyframe) one-shots rendered by the EffectsLayer.
 * Each root element owns one finite animation and reports `effectsBus.finish(id)` on its own
 * animationend (child animationends are ignored), with a timeout fallback so a tier switch to `off`
 * mid-flight (animation: none, no event) can never leave the layer mounted.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EffectRect } from './effectsBus';
import { effectsBus } from './effectsBus';
import styles from './EffectsLayer.module.css';

const useFinishOnEnd = (id: number, fallbackMs: number): ((event: React.AnimationEvent) => void) => {
  useEffect(() => {
    const timer = window.setTimeout(() => effectsBus.finish(id), fallbackMs);
    return () => window.clearTimeout(timer);
  }, [id, fallbackMs]);

  return useCallback(
    (event: React.AnimationEvent) => {
      if (event.target === event.currentTarget) effectsBus.finish(id);
    },
    [id],
  );
};

interface EffectProps {
  id: number;
}

export const RevealCurtain: React.FC<EffectProps> = ({ id }) => {
  const onAnimationEnd = useFinishOnEnd(id, 1400);
  return (
    <div className={styles.reveal} onAnimationEnd={onAnimationEnd}>
      <div className={styles.sweep} />
    </div>
  );
};

const TUNNEL_BASE_VMIN = 0.4;

export const GameOpenTunnel: React.FC<EffectProps & { rect: EffectRect }> = ({ id, rect }) => {
  const onAnimationEnd = useFinishOnEnd(id, 900);

  // Scale the 40vmin disc until its radius reaches the farthest viewport corner.
  const style = useMemo(() => {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const farthest = Math.max(Math.hypot(cx, cy), Math.hypot(vw - cx, cy), Math.hypot(cx, vh - cy), Math.hypot(vw - cx, vh - cy));
    const base = TUNNEL_BASE_VMIN * Math.min(vw, vh);
    const scale = ((2 * farthest) / base) * 1.15;
    return {
      '--fx-x': `${cx.toFixed(1)}px`,
      '--fx-y': `${cy.toFixed(1)}px`,
      '--fx-scale': scale.toFixed(2),
    } as React.CSSProperties;
  }, [rect]);

  return <div className={styles.tunnel} style={style} onAnimationEnd={onAnimationEnd} />;
};

export const AmountBurst: React.FC<EffectProps & { amount: string; label: string }> = ({ id, amount, label }) => {
  const { t } = useTranslation();
  const onAnimationEnd = useFinishOnEnd(id, 2800);
  return (
    <div className={styles.burst} onAnimationEnd={onAnimationEnd}>
      {label && <span className={styles.burstLabel}>{t(label, label)}</span>}
      {amount && <span className={styles.burstAmount}>{amount}</span>}
    </div>
  );
};
